import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import SpotlightTour from './SpotlightTour';

/* First-launch tour. Game-style coach marks that light up the real buttons
 * one by one instead of a wall of text. Replay it from Help. */

const TEXT = {
    en: {
        labels: { next: 'Next', back: 'Back', skip: 'Skip tour', done: 'Start growing 🌱' },
        steps: [
            [null, '🌱', 'Welcome to GrowDaily', 'Your garden, played like a game. Here is a 30-second tour of where everything lives.'],
            ['[data-testid="nav-home"]', '🏡', 'Home', "Today at a glance: the weather, your tasks and shortcuts to everything you use most."],
            ['[data-testid="nav-garden"]', '🪴', 'My Garden', 'Add your plants with photos, track their health and water them all with one tap.'],
            ['[data-testid="nav-tasks"]', '✅', 'Tasks', 'Watering, feeding and harvest reminders. Tick them off and earn Muld coins for Eden.'],
            ['[data-testid="nav-doctor"]', '🩺', 'Garden Doctor', 'Snap a photo of a struggling plant and get clear advice on what to do.'],
            ['[data-testid="nav-more"]', '🎮', 'More: games & guides', 'Eden (your garden as a game), Recipes with freezing and pickling guides, the Wild Food guide, the Finds map and more.'],
            ['[data-testid="nav-profile"]', '⚙️', 'Profile', 'Theme, language, Simple or Advanced view and your account.'],
            [null, '🏆', "You're ready!", 'Tip: add the "Snap a find" shortcut to your home screen to identify and log finds straight from the camera. Replay this tour from Help any time.'],
        ],
    },
    da: {
        labels: { next: 'Næste', back: 'Tilbage', skip: 'Spring over', done: 'Kom i gang 🌱' },
        steps: [
            [null, '🌱', 'Velkommen til GrowDaily', 'Din have – spillet som et spil. Her er en rundvisning på 30 sekunder, så du ved, hvor alt ligger.'],
            ['[data-testid="nav-home"]', '🏡', 'Hjem', 'Dagens overblik: vejret, dine opgaver og genveje til det, du bruger mest.'],
            ['[data-testid="nav-garden"]', '🪴', 'Min Have', 'Tilføj dine planter med billeder, følg deres sundhed, og vand dem alle med ét tryk.'],
            ['[data-testid="nav-tasks"]', '✅', 'Opgaver', 'Vanding, gødning og høst. Sæt flueben, og optjen Muld-mønter til Eden.'],
            ['[data-testid="nav-doctor"]', '🩺', 'Havedoktor', 'Tag et billede af en plante, der har det svært, og få klare råd om, hvad du skal gøre.'],
            ['[data-testid="nav-more"]', '🎮', 'Mere: spil & guider', 'Eden (din have som spil), Opskrifter med guider til frysning og syltning, Vild mad-guiden, Fundkortet og meget mere.'],
            ['[data-testid="nav-profile"]', '⚙️', 'Profil', 'Tema, sprog, Simpel eller Avanceret visning og din konto.'],
            [null, '🏆', 'Du er klar!', 'Tip: læg genvejen "Snap et fund" på hjemmeskærmen, så du kan identificere og logge fund direkte fra kameraet. Rundvisningen kan altid startes igen fra Hjælp.'],
        ],
    },
};

export default function TutorialModal() {
    const { user } = useAuth();
    const { locale } = useI18n();
    const [open, setOpen] = useState(false);
    const copy = TEXT[locale === 'da' ? 'da' : 'en'];
    const steps = copy.steps.map(([target, emoji, title, body]) => ({ target, emoji, title, body }));
    const eligible = Boolean(user && !user.is_guest);

    useEffect(() => {
        if (!eligible) { setOpen(false); return undefined; }
        // Changing display mode must never interrupt a task with another tour.
        // Honour both old mode-specific completion keys on upgraded installs.
        const onPremium = window.location.pathname.startsWith('/premium');
        try {
            const completed = ['gd_tutorial_done', 'gd_tutorial_done_full', 'gd_tutorial_done_lightweight']
                .some(key => localStorage.getItem(key));
            if (!onPremium && !completed) setOpen(true);
        } catch { /* The manual Help action still works when storage is blocked. */ }
        const onOpen = () => setOpen(true);
        window.addEventListener('open-tutorial', onOpen);
        return () => window.removeEventListener('open-tutorial', onOpen);
    }, [eligible]);

    const finish = () => {
        try { localStorage.setItem('gd_tutorial_done', '1'); } catch { /* ignore */ }
        setOpen(false);
    };

    // Only show the tour for real logged-in users (not anonymous visitors or guests)
    if (!eligible) return null;
    return <SpotlightTour open={open} onClose={finish} steps={steps} labels={copy.labels} testid="tutorial-modal" idPrefix="tutorial" />;
}

export function launchTutorial() {
    window.dispatchEvent(new Event('open-tutorial'));
}
